import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import type { StratumId } from '@somnhold/content';
import { DIRS, hexToWorld, type DungeonLayout, type SimaPropSpot, type Zone } from '@somnhold/sim';
import { WORLD_PER_HEX } from './coords.ts';

/*
 * The Chasm's props: what stands in each stratum (niches and bones, basalt, glass, columns and chains, roots,
 * mushrooms, ice, crystal, shelves and books, anvils and hammers, silk and cocoons, ribs), all of a floor merged into
 * ONE mesh with its colour, glow and gloss in the vertices, drawn with the one props material (engine/sima.ts). A floor
 * of props is one draw call whatever the stratum; going down drops the old geometry and builds the next.
 *
 * Diorama scale: the crypt's walls stand under a metre (WALL_H 0.72), there is no ceiling. What "hangs from the
 * ceiling" in docs/SIMA.md hangs here off the wall tops, off beams between columns or arches over a room's rim, never
 * over the middle where the fight is.
 */

const K = WORLD_PER_HEX / 1000;
const S = WORLD_PER_HEX;
const TAU = Math.PI * 2;

/** A shape's triangles, unindexed, positions only (flat shading takes the normals from them). */
const flat = (g: THREE.BufferGeometry): Float32Array => {
  const n = g.index ? g.toNonIndexed() : g;
  const a = (n.getAttribute('position') as THREE.BufferAttribute).array as Float32Array;
  const out = new Float32Array(a);
  g.dispose();
  if (n !== g) n.dispose();
  return out;
};

const merged = (parts: THREE.BufferGeometry[]): THREE.BufferGeometry => {
  const m = mergeGeometries(parts.map((p) => (p.index ? p.toNonIndexed() : p))) as THREE.BufferGeometry;
  for (const p of parts) p.dispose();
  return m;
};

let shapes: Record<string, Float32Array> | null = null;
/** The building blocks, made once: unit sizes, bases at y = 0 unless said. */
const SHAPES = (): Record<string, Float32Array> => {
  if (shapes) return shapes;
  shapes = {
    box: flat(new THREE.BoxGeometry(1, 1, 1).translate(0, 0.5, 0)),
    cyl6: flat(new THREE.CylinderGeometry(1, 1, 1, 6).translate(0, 0.5, 0)),
    cyl8: flat(new THREE.CylinderGeometry(1, 1, 1, 8).translate(0, 0.5, 0)),
    /** A tapered limb from y 0 (thick) to 1 (thin): bones, roots, icicles laid on a segment. */
    limb: flat(new THREE.CylinderGeometry(0.55, 1, 1, 5).translate(0, 0.5, 0)),
    cone5: flat(new THREE.ConeGeometry(1, 1, 5).translate(0, 0.5, 0)),
    cone6: flat(new THREE.ConeGeometry(1, 1, 6).translate(0, 0.5, 0)),
    rock: flat(new THREE.DodecahedronGeometry(1, 0)),
    ico: flat(new THREE.IcosahedronGeometry(1, 0)),
    ball: flat(new THREE.SphereGeometry(1, 7, 5)),
    dome: flat(new THREE.SphereGeometry(1, 10, 4, 0, TAU, 0, Math.PI / 2)),
    gills: flat(new THREE.CircleGeometry(1, 10).rotateX(Math.PI / 2)),
    link: flat(new THREE.TorusGeometry(1, 0.3, 4, 6)),
    arch: flat(new THREE.TorusGeometry(1, 0.13, 5, 10, Math.PI)),
    /** A crystal: a hex prism with a point, base at 0, 1.6 tall. */
    prism: flat(merged([new THREE.CylinderGeometry(1, 1, 1, 6).translate(0, 0.5, 0), new THREE.ConeGeometry(1, 0.6, 6).translate(0, 1.3, 0)])),
    /** A skull: a ball and a jaw. */
    skull: flat(merged([new THREE.SphereGeometry(1, 7, 5).scale(1, 0.85, 1.1), new THREE.BoxGeometry(1.1, 0.5, 0.9).translate(0, -0.55, 0.35)])),
    /** An anvil: foot, waist, face and horn. */
    anvil: flat(
      merged([
        new THREE.BoxGeometry(0.9, 0.22, 0.6).translate(0, 0.11, 0),
        new THREE.BoxGeometry(0.45, 0.3, 0.32).translate(0, 0.37, 0),
        new THREE.BoxGeometry(1.1, 0.22, 0.48).translate(0, 0.63, 0),
        new THREE.ConeGeometry(0.2, 0.55, 5).rotateZ(Math.PI / 2).translate(-0.8, 0.66, 0),
      ]),
    ),
    /** A cocoon: a long egg. */
    egg: flat(new THREE.SphereGeometry(1, 7, 6).scale(1, 1.8, 1)),
    plane: flat(new THREE.PlaneGeometry(1, 1)),
  };
  return shapes;
};

const m4 = new THREE.Matrix4();
const q4 = new THREE.Quaternion();
const e4 = new THREE.Euler();
const v4 = new THREE.Vector3();
const s4 = new THREE.Vector3();
const UP = new THREE.Vector3(0, 1, 0);
const col = new THREE.Color();

/** Collects shapes in place into one geometry: position, colour, and per vertex [glow, gloss]. */
export class Kit {
  private pos: number[] = [];
  private col: number[] = [];
  private mat: number[] = [];

  add(shape: string, m: THREE.Matrix4, color: THREE.ColorRepresentation, glow = 0, gloss = 0): void {
    const src = SHAPES()[shape];
    if (!src) return;
    col.set(color);
    const e = m.elements;
    for (let i = 0; i < src.length; i += 3) {
      const x = src[i] as number, y = src[i + 1] as number, z = src[i + 2] as number;
      this.pos.push(e[0]! * x + e[4]! * y + e[8]! * z + e[12]!, e[1]! * x + e[5]! * y + e[9]! * z + e[13]!, e[2]! * x + e[6]! * y + e[10]! * z + e[14]!);
      this.col.push(col.r, col.g, col.b);
      this.mat.push(glow, gloss);
    }
  }

  /** A shape at (x, y, z), turned (rx, ry, rz) and scaled. */
  at(shape: string, x: number, y: number, z: number, rx: number, ry: number, rz: number, sx: number, sy: number, sz: number, color: THREE.ColorRepresentation, glow = 0, gloss = 0): void {
    e4.set(rx, ry, rz);
    q4.setFromEuler(e4);
    m4.compose(v4.set(x, y, z), q4, s4.set(sx, sy, sz));
    this.add(shape, m4, color, glow, gloss);
  }

  /** A shape stretched from a to b (its y axis along the segment), `r` thick (x and z). */
  seg(shape: string, a: THREE.Vector3, b: THREE.Vector3, r: number, color: THREE.ColorRepresentation, glow = 0, gloss = 0, rz = r): void {
    const d = v4.subVectors(b, a);
    const len = d.length();
    if (len < 1e-4) return;
    q4.setFromUnitVectors(UP, d.multiplyScalar(1 / len));
    m4.compose(a, q4, s4.set(r, len, rz));
    this.add(shape, m4, color, glow, gloss);
  }

  get empty(): boolean {
    return this.pos.length === 0;
  }

  geometry(): THREE.BufferGeometry {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(this.pos, 3));
    g.setAttribute('color', new THREE.Float32BufferAttribute(this.col, 3));
    g.setAttribute('aMat', new THREE.Float32BufferAttribute(this.mat, 2));
    g.computeBoundingSphere();
    return g;
  }
}

/* ---------- where things go ---------- */

/** A wall's face toward the floor: its middle (world) and the way into the room. */
export interface Face {
  x: number;
  z: number;
  nx: number;
  nz: number;
}
export interface RoomSpot {
  i: number;
  kind: 'entry' | 'room' | 'boss';
  x: number;
  z: number;
  /** Radius in world units (to the last floor ring's middle). */
  r: number;
  faces: Face[];
  /** Floor cells near the rim (world), free of doors and puzzles. */
  rim: [number, number][];
}

/** The rooms of a floor, their wall faces and their rim cells, away from doors and the given spots. */
export const roomSpots = (L: DungeonLayout, avoid: readonly [number, number][]): RoomSpot[] => {
  const floor = new Set(L.floor.map((h) => `${h.q},${h.r}`));
  const doors = L.doors.map((d) => {
    const w = hexToWorld(d.q, d.r);
    return [w.x * K, w.y * K] as [number, number];
  });
  const near = (x: number, z: number, list: readonly [number, number][], d: number): boolean => list.some(([a, b]) => (a - x) ** 2 + (b - z) ** 2 < d * d);
  return L.rooms.map((rm, i) => {
    const c = hexToWorld(rm.q, rm.r);
    const out: RoomSpot = { i, kind: rm.kind, x: c.x * K, z: c.y * K, r: rm.radius * S * 0.87, faces: [], rim: [] };
    for (let dq = -rm.radius; dq <= rm.radius; dq++) {
      for (let dr = -rm.radius; dr <= rm.radius; dr++) {
        const ring = (Math.abs(dq) + Math.abs(dr) + Math.abs(dq + dr)) / 2;
        if (ring > rm.radius) continue;
        const q = rm.q + dq, r = rm.r + dr;
        if (!floor.has(`${q},${r}`)) continue;
        const w = hexToWorld(q, r);
        const x = w.x * K, z = w.y * K;
        if (ring >= rm.radius - 1 && ring > 0 && !near(x, z, doors, 1.9) && !near(x, z, avoid, 1.3)) out.rim.push([x, z]);
        for (const d of DIRS) {
          if (floor.has(`${q + d.q},${r + d.r}`)) continue;
          const o = hexToWorld(q + d.q, r + d.r);
          const ox = o.x * K, oz = o.y * K;
          const fx = (x + ox) / 2, fz = (z + oz) / 2;
          if (near(fx, fz, doors, 1.5)) continue;
          const l = Math.hypot(x - ox, z - oz) || 1;
          out.faces.push({ x: fx, z: fz, nx: (x - ox) / l, nz: (z - oz) / l });
        }
      }
    }
    // Round the room, so "every few faces" spreads evenly.
    out.faces.sort((a, b) => Math.atan2(a.z - out.z, a.x - out.x) - Math.atan2(b.z - out.z, b.x - out.x));
    return out;
  });
};

/** What the floor's hazards and props say must stand somewhere (resting emitters, anvils, the pool). */
export interface PropCtx {
  rooms: RoomSpot[];
  rnd: () => number;
  low: boolean;
  /** Hazard spots that rest between strikes (no `warn`, `every` > 0), and their kind. */
  emitters: readonly Zone[];
  props: readonly SimaPropSpot[];
}

/** Trip hammers (forge): their heads move, so they are instances of their own (see SimaLook). */
export interface Built {
  geometry: THREE.BufferGeometry;
  hammers: THREE.Vector3[];
  /** Niches and crystals that glow red while their strike is marked. */
  marks: { x: number; z: number; y: number; haz: string }[];
}

const yawOf = (f: Face): number => Math.atan2(f.nx, f.nz);
const pick = <T>(rnd: () => number, a: readonly T[]): T => a[Math.floor(rnd() * a.length)] as T;
const shade = (c: string, rnd: () => number, k = 0.18): THREE.Color => new THREE.Color(c).multiplyScalar(1 - k / 2 + rnd() * k);
const every = <T>(a: readonly T[], n: number, off = 0): T[] => a.filter((_, i) => (i + off) % n === 0);
/** A face's spot on the floor, `d` in from the wall, `s` along it. */
const onFace = (f: Face, d: number, s = 0, y = 0): THREE.Vector3 => new THREE.Vector3(f.x + f.nx * d - f.nz * s, y, f.z + f.nz * d + f.nx * s);
const nearestFace = (rooms: RoomSpot[], x: number, z: number): Face | null => {
  let best: Face | null = null;
  let bd = Infinity;
  for (const rm of rooms) for (const f of rm.faces) {
    const d = (f.x - x) ** 2 + (f.z - z) ** 2;
    if (d < bd) {
      bd = d;
      best = f;
    }
  }
  return best;
};

const WALL_TOP = 0.78;

/* ---------- each stratum ---------- */

type Dresser = (k: Kit, c: PropCtx, out: Built) => void;

const BONE = '#a9adb2';
const BONE_D = '#7c8086';

const bones = (k: Kit, rnd: () => number, p: THREE.Vector3, n: number): void => {
  for (let j = 0; j < n; j++) {
    const a = rnd() * TAU;
    const l = 0.18 + rnd() * 0.16;
    const x = p.x + (rnd() - 0.5) * 0.5, z = p.z + (rnd() - 0.5) * 0.5;
    k.at('limb', x, 0.03, z, Math.PI / 2, a, 0, 0.025, l, 0.025, shade(BONE, rnd));
  }
};
const skull = (k: Kit, rnd: () => number, x: number, y: number, z: number, s = 0.075, yaw = rnd() * TAU): void => k.at('skull', x, y + s * 0.8, z, 0, yaw, (rnd() - 0.5) * 0.4, s, s, s, shade('#b4b8be', rnd));
const candles = (k: Kit, rnd: () => number, p: THREE.Vector3, n: number, wax = '#c4c8ce', flame = '#ffb347'): void => {
  for (let j = 0; j < n; j++) {
    const x = p.x + (rnd() - 0.5) * 0.4, z = p.z + (rnd() - 0.5) * 0.4;
    const h = 0.08 + rnd() * 0.16;
    k.at('cyl6', x, 0, z, 0, 0, 0, 0.032, h, 0.032, shade(wax, rnd, 0.1));
    k.at('ball', x, h + 0.025, z, 0, 0, 0, 0.022, 0.035, 0.022, flame, 3);
  }
};
const rubble = (k: Kit, rnd: () => number, p: THREE.Vector3, n: number, c: string, spread = 0.5): void => {
  for (let j = 0; j < n; j++) {
    const s = 0.05 + rnd() * 0.09;
    k.at('rock', p.x + (rnd() - 0.5) * spread, s * 0.5, p.z + (rnd() - 0.5) * spread, rnd() * 3, rnd() * 3, rnd() * 3, s, s * 0.8, s, shade(c, rnd, 0.3));
  }
};

/** A niche cut into the wall: a dark arched recess with a skull and bones in it. */
const niche = (k: Kit, rnd: () => number, f: Face, out: Built): void => {
  const yaw = yawOf(f);
  const p = onFace(f, -0.02);
  k.at('box', p.x, 0.05, p.z, 0, yaw, 0, 0.62, 0.62, 0.1, '#040405');
  k.at('box', p.x - f.nz * 0.36, 0, p.z + f.nx * 0.36, 0, yaw, 0, 0.12, 0.74, 0.16, '#3c4250');
  k.at('box', p.x + f.nz * 0.36, 0, p.z - f.nx * 0.36, 0, yaw, 0, 0.12, 0.74, 0.16, '#3c4250');
  k.at('arch', p.x + f.nx * 0.02, 0.66, p.z + f.nz * 0.02, 0, yaw, 0, 0.36, 0.3, 0.5, '#3c4250');
  const s = onFace(f, 0.06, 0, 0.1);
  skull(k, rnd, s.x, 0.08, s.z, 0.085, yaw);
  k.at('limb', s.x - f.nz * 0.15, 0.1, s.z + f.nx * 0.15, 0, yaw, Math.PI / 2, 0.025, 0.3, 0.025, BONE_D);
  out.marks.push({ x: s.x, z: s.z, y: 0.4, haz: 'niche' });
};

const ossuary: Dresser = (k, { rooms, rnd, emitters }, out) => {
  const niches = new Set<Face>();
  for (const z of emitters) if (z.haz === 'niche') {
    const f = nearestFace(rooms, z.x * K, z.y * K);
    if (f && !niches.has(f)) {
      niches.add(f);
      niche(k, rnd, f, out);
    }
  }
  for (const rm of rooms) {
    // Niches all round the walls (only the marked ones open), bones and candles at their feet.
    for (const f of every(rm.faces, rm.kind === 'boss' ? 3 : 4, rm.i)) if (!niches.has(f)) niche(k, rnd, f, out);
    for (const f of every(rm.faces, 5, rm.i + 2)) {
      const p = onFace(f, 0.4, (rnd() - 0.5) * 0.4);
      const roll = rnd();
      if (roll < 0.4) {
        bones(k, rnd, p, 4);
        skull(k, rnd, p.x, 0, p.z);
      } else if (roll < 0.75) candles(k, rnd, p, 3);
      else rubble(k, rnd, p, 4, '#3a3e48');
    }
    if (rm.kind === 'boss') {
      // Sarcophagi along the hall's walls, the guardian's bier.
      for (const f of every(rm.faces, 7, 3)) {
        const p = onFace(f, 0.75);
        const yaw = yawOf(f) + Math.PI / 2;
        k.at('box', p.x, 0, p.z, 0, yaw, 0, 1.1, 0.34, 0.5, shade('#3a404c', rnd));
        k.at('box', p.x, 0.34, p.z, 0, yaw, 0, 1.18, 0.08, 0.58, shade('#4a5262', rnd));
      }
    }
  }
};

const marrow: Dresser = (k, { rooms, rnd }) => {
  for (const rm of rooms) {
    // Basalt columns in clusters against the walls, their tops split by fire; glowing clinker at their feet.
    for (const f of every(rm.faces, 3, rm.i)) {
      const n = 2 + Math.floor(rnd() * 3);
      for (let j = 0; j < n; j++) {
        const p = onFace(f, 0.25 + rnd() * 0.25, (rnd() - 0.5) * 0.7);
        const h = 0.4 + rnd() * (rm.kind === 'boss' ? 1.4 : 0.9);
        const r = 0.11 + rnd() * 0.08;
        k.at('cyl6', p.x, 0, p.z, 0, rnd(), 0, r, h, r, shade('#1c1716', rnd, 0.3));
        k.at('cyl6', p.x, h, p.z, 0, rnd(), 0, r * 0.82, 0.03, r * 0.82, '#ff5a1a', 2.2);
      }
      if (rnd() < 0.5) {
        const p = onFace(f, 0.8, (rnd() - 0.5) * 0.6);
        for (let j = 0; j < 3; j++) {
          const s = 0.05 + rnd() * 0.07;
          k.at('rock', p.x + (rnd() - 0.5) * 0.4, s * 0.4, p.z + (rnd() - 0.5) * 0.4, rnd() * 3, rnd() * 3, 0, s, s * 0.7, s, rnd() < 0.5 ? '#ff6a24' : '#2a1a14', rnd() < 0.5 ? 1.8 : 0);
        }
      }
    }
  }
};

const glass: Dresser = (k, { rooms, rnd }) => {
  for (const rm of rooms) {
    // Blades of black glass leaning out of the walls, in sheaves.
    for (const f of every(rm.faces, 3, rm.i + 1)) {
      const n = 2 + Math.floor(rnd() * 3);
      for (let j = 0; j < n; j++) {
        const p = onFace(f, 0.15 + rnd() * 0.3, (rnd() - 0.5) * 0.8);
        const h = 0.4 + rnd() * (rm.kind === 'boss' ? 1.6 : 1.0);
        const r = 0.06 + rnd() * 0.08;
        const lean = 0.15 + rnd() * 0.35;
        k.at('cone5', p.x, 0, p.z, f.nz * lean, rnd() * TAU, -f.nx * lean, r, h, r, shade('#0e0b16', rnd, 0.4), 0.15, 1);
      }
    }
    for (const f of every(rm.faces, 6, rm.i)) {
      const p = onFace(f, 0.7);
      for (let j = 0; j < 4; j++) k.at('cone5', p.x + (rnd() - 0.5) * 0.5, 0, p.z + (rnd() - 0.5) * 0.5, (rnd() - 0.5) * 2.4, rnd() * TAU, (rnd() - 0.5) * 2.4, 0.025, 0.14, 0.025, '#2a2040', 0.4, 1);
    }
  }
};

const cisterns: Dresser = (k, { rooms, rnd }) => {
  for (const rm of rooms) {
    // Columns round the rim under round arches back to the wall; chains hang from the arches; a grate or two.
    const faces = every(rm.faces, rm.kind === 'boss' ? 3 : 4, rm.i);
    for (const f of faces) {
      const p = onFace(f, 0.95);
      const h = rm.kind === 'boss' ? 2.3 : 1.9;
      const c = shade('#2e3e40', rnd, 0.15);
      k.at('box', p.x, 0, p.z, 0, yawOf(f), 0, 0.42, 0.14, 0.42, '#1e2a2c');
      k.at('cyl8', p.x, 0.14, p.z, 0, 0, 0, 0.16, h - 0.3, 0.16, c);
      k.at('box', p.x, h - 0.16, p.z, 0, yawOf(f), 0, 0.42, 0.18, 0.42, '#1e2a2c');
      // The arch: from the column's top to the wall behind it.
      const back = onFace(f, -0.1);
      const mid = p.clone().add(back).multiplyScalar(0.5);
      const span = p.distanceTo(back) / 2;
      k.at('arch', mid.x, h - 0.05, mid.z, 0, yawOf(f) + Math.PI / 2, 0, span, span * 0.7, 0.9, '#25332f');
      k.at('box', back.x, 0, back.z, 0, yawOf(f), 0, 0.3, h + 0.15, 0.2, '#1a2426');
      // A chain hanging from the arch's crown, a few links ending above the water.
      const top = h - 0.1 + span * 0.7;
      const links = 5 + Math.floor(rnd() * 4);
      for (let j = 0; j < links; j++) k.at('link', mid.x, top - 0.1 - j * 0.11, mid.z, 0, (j % 2) * (Math.PI / 2), 0, 0.045, 0.06, 0.045, '#1b2224', 0, 0.6);
    }
    for (const f of every(rm.faces, 7, rm.i + 3)) {
      const p = onFace(f, 0.35);
      k.at('box', p.x, 0, p.z, 0, yawOf(f), 0, 0.5, 0.05, 0.5, '#141a1b', 0, 0.5);
      for (let j = -2; j <= 2; j++) k.at('box', p.x - f.nz * j * 0.09, 0.05, p.z + f.nx * j * 0.09, 0, yawOf(f), 0, 0.03, 0.03, 0.46, '#2a3436', 0, 0.6);
    }
  }
};

/** A root from a to b, tapering, with a few rootlets. */
const root = (k: Kit, rnd: () => number, a: THREE.Vector3, b: THREE.Vector3, r: number): void => {
  const mid = a.clone().lerp(b, 0.5).add(new THREE.Vector3((rnd() - 0.5) * 0.3, 0.15 + rnd() * 0.2, (rnd() - 0.5) * 0.3));
  const c = shade('#2b1d13', rnd, 0.3);
  k.seg('limb', a, mid, r, c);
  k.seg('limb', mid, b, r * 0.6, c);
  for (let j = 0; j < 2; j++) {
    const t = mid.clone().lerp(b, rnd());
    k.seg('limb', t, t.clone().add(new THREE.Vector3((rnd() - 0.5) * 0.5, -0.2 - rnd() * 0.3, (rnd() - 0.5) * 0.5)), r * 0.25, c);
  }
};

const buried: Dresser = (k, { rooms, rnd, emitters }) => {
  for (const rm of rooms) {
    // Thick roots spill over the wall tops and down into the room; moss heaps; a fallen trunk in the hall.
    for (const f of every(rm.faces, 2, rm.i)) {
      if (rnd() < 0.35) continue;
      const a = onFace(f, -0.3, (rnd() - 0.5) * 0.4, WALL_TOP + 0.05);
      const b = onFace(f, 0.6 + rnd() * 0.7, (rnd() - 0.5) * 0.8, 0.02);
      root(k, rnd, a, b, 0.1 + rnd() * 0.08);
    }
    for (const f of every(rm.faces, 3, rm.i + 1)) {
      const p = onFace(f, 0.3, (rnd() - 0.5) * 0.6);
      for (let j = 0; j < 3; j++) {
        const s = 0.1 + rnd() * 0.14;
        k.at('rock', p.x + (rnd() - 0.5) * 0.5, s * 0.25, p.z + (rnd() - 0.5) * 0.5, 0, rnd() * 3, 0, s, s * 0.45, s, shade('#355e22', rnd, 0.3));
      }
      // Hanging moss off the wall's lip.
      const lip = onFace(f, 0.02, (rnd() - 0.5) * 0.5, WALL_TOP);
      k.at('cone5', lip.x, lip.y - 0.35, lip.z, Math.PI, rnd() * TAU, 0, 0.06, 0.35, 0.06, '#2f5a1e');
    }
    if (rm.kind === 'boss' && rm.faces.length) {
      const f = pick(rnd, rm.faces);
      const a = onFace(f, 0.6, -1.2, 0.18);
      const b = onFace(f, 0.9, 1.2, 0.18);
      k.seg('cyl8', a, b, 0.2, '#2a1e14');
    }
  }
  // Where roots strike: a knot of roots breaking the floor.
  for (const z of emitters) if (z.haz === 'roots') {
    const x = z.x * K, y = z.y * K;
    for (let j = 0; j < 5; j++) {
      const a = (j / 5) * TAU + rnd();
      const p0 = new THREE.Vector3(x + Math.cos(a) * 0.15, 0.02, y + Math.sin(a) * 0.15);
      const p1 = new THREE.Vector3(x + Math.cos(a) * 0.7, 0.01, y + Math.sin(a) * 0.7);
      k.seg('limb', p0, p1, 0.05, '#2b1d13');
    }
    k.at('rock', x, 0.02, y, 0, rnd() * 3, 0, 0.22, 0.12, 0.22, '#1a120c');
  }
};

/** A giant mushroom: a pale stem, a dark wide cap with glowing gills under it. */
const mushroom = (k: Kit, rnd: () => number, x: number, z: number, h: number, r: number, lean: number, yaw: number): void => {
  const stem = r * 0.22;
  const tip = new THREE.Vector3(x + Math.sin(yaw) * lean * h, h, z + Math.cos(yaw) * lean * h);
  k.seg('cyl6', new THREE.Vector3(x, 0, z), tip, stem, shade('#a8b4c8', rnd, 0.15));
  k.at('dome', tip.x, tip.y - 0.02, tip.z, 0, rnd(), 0, r, r * 0.45, r, shade('#1c4a6a', rnd, 0.3), 0.15);
  k.at('gills', tip.x, tip.y - 0.015, tip.z, 0, 0, 0, r * 0.94, 1, r * 0.94, '#4fd6ff', 1.6);
  // Spots on the cap.
  for (let j = 0; j < 3; j++) {
    const a = rnd() * TAU, d = rnd() * r * 0.6;
    k.at('ball', tip.x + Math.cos(a) * d, tip.y + r * 0.42 * (1 - (d / r) ** 2) - 0.01, tip.z + Math.sin(a) * d, 0, 0, 0, r * 0.08, r * 0.04, r * 0.08, '#7fe8ff', 1.4);
  }
};

const grotto: Dresser = (k, { rooms, rnd }) => {
  for (const rm of rooms) {
    // Giant mushrooms leaning out of the walls, clusters of small ones on the rim, glowing beads on the floor.
    for (const f of every(rm.faces, rm.kind === 'boss' ? 3 : 4, rm.i)) {
      const p = onFace(f, 0.4, (rnd() - 0.5) * 0.4);
      const big = rm.kind === 'boss' ? 1.5 : 1;
      mushroom(k, rnd, p.x, p.z, (0.9 + rnd() * 0.9) * big, (0.45 + rnd() * 0.35) * big, 0.15 + rnd() * 0.15, Math.atan2(f.nx, f.nz));
    }
    for (const f of every(rm.faces, 3, rm.i + 1)) {
      const p = onFace(f, 0.3, (rnd() - 0.5) * 0.6);
      for (let j = 0; j < 3; j++) mushroom(k, rnd, p.x + (rnd() - 0.5) * 0.5, p.z + (rnd() - 0.5) * 0.5, 0.12 + rnd() * 0.25, 0.08 + rnd() * 0.1, rnd() * 0.3, rnd() * TAU);
    }
  }
};

const frost: Dresser = (k, { rooms, rnd }) => {
  for (const rm of rooms) {
    // Icicles off every wall's lip; blades of blue ice and frozen boulders at the walls.
    for (const f of rm.faces) {
      if (rnd() < 0.45) continue;
      for (let j = 0; j < 2; j++) {
        const lip = onFace(f, 0.03, (rnd() - 0.5) * 0.6, WALL_TOP + 0.02);
        const l = 0.18 + rnd() * 0.35;
        const r = 0.035 + rnd() * 0.02;
        // Point down: the cone turned over, its base on the lip.
        k.at('cone5', lip.x, lip.y, lip.z, Math.PI, rnd() * TAU, 0, r, l, r, shade('#cfe6ff', rnd, 0.1), 0.35, 1);
      }
    }
    for (const f of every(rm.faces, 3, rm.i + 2)) {
      const n = 2 + Math.floor(rnd() * 2);
      for (let j = 0; j < n; j++) {
        const p = onFace(f, 0.2 + rnd() * 0.35, (rnd() - 0.5) * 0.7);
        const h = 0.3 + rnd() * (rm.kind === 'boss' ? 1.3 : 0.7);
        const r = 0.08 + rnd() * 0.08;
        k.at('cone6', p.x, 0, p.z, f.nz * 0.3 * rnd(), rnd() * TAU, -f.nx * 0.3 * rnd(), r, h, r, shade('#8ab8ec', rnd, 0.25), 0.35, 1);
      }
      const b = onFace(f, 0.75, (rnd() - 0.5) * 0.5);
      k.at('ico', b.x, 0.1, b.z, rnd(), rnd(), rnd(), 0.16, 0.12, 0.16, '#b8d6f4', 0.2, 1);
    }
  }
};

/** A cluster of crystals: hex prisms with points, leaning out of one spot. */
const crystals = (k: Kit, rnd: () => number, p: THREE.Vector3, n: number, size: number, color: string, glow: number, out?: { lean: THREE.Vector3 }): void => {
  for (let j = 0; j < n; j++) {
    const r = (0.05 + rnd() * 0.06) * size;
    const h = (0.25 + rnd() * 0.5) * size;
    const lx = out ? out.lean.x * 0.5 : 0, lz = out ? out.lean.z * 0.5 : 0;
    k.at('prism', p.x + (rnd() - 0.5) * 0.25 * size, 0, p.z + (rnd() - 0.5) * 0.25 * size, lz + (rnd() - 0.5) * 0.7, rnd() * TAU, -lx + (rnd() - 0.5) * 0.7, r, h / 1.6, r, shade(color, rnd, 0.3), glow, 1);
  }
};

const geode: Dresser = (k, { rooms, rnd, emitters }, out) => {
  for (const rm of rooms) {
    // Crystal grown out of every wall: violet mostly, green now and then; great ones in the hall.
    for (const f of every(rm.faces, 2, rm.i)) {
      const p = onFace(f, 0.15 + rnd() * 0.2, (rnd() - 0.5) * 0.5);
      const green = rnd() < 0.3;
      crystals(k, rnd, p, 3 + Math.floor(rnd() * 3), rm.kind === 'boss' ? 2 : 1.3, green ? '#3adf8a' : '#b45aff', green ? 0.9 : 0.7, { lean: new THREE.Vector3(f.nx, 0, f.nz) });
    }
  }
  // The beams' ends: two tall crystals, one at each end of each beam (they flare when it is marked).
  for (const z of emitters) if (z.haz === 'beam') {
    for (const [x, y] of [[z.x, z.y], [z.tx ?? z.x, z.ty ?? z.y]] as const) {
      const p = new THREE.Vector3(x * K, 0, y * K);
      k.at('prism', p.x, 0, p.z, 0, rnd(), 0, 0.16, 0.8, 0.16, '#e8a0ff', 1.3, 1);
      crystals(k, rnd, p, 3, 0.9, '#c070ff', 0.8);
      out.marks.push({ x: p.x, z: p.z, y: 1.1, haz: 'beam' });
    }
  }
};

const BOOKS = ['#5a1e24', '#22344c', '#2c3c2a', '#3a3a42', '#1e2430', '#4a2a3a'];
const archive: Dresser = (k, { rooms, rnd }) => {
  for (const rm of rooms) {
    // Shelves fallen against the walls, books in heaps and stacks, candles, red wax, an ink pot spilt.
    for (const f of every(rm.faces, 3, rm.i)) {
      const p = onFace(f, 0.35, (rnd() - 0.5) * 0.3);
      const yaw = yawOf(f) + Math.PI / 2;
      const tilt = rnd() < 0.5 ? 0.9 + rnd() * 0.5 : 0.15;
      const wood = shade('#2a2522', rnd, 0.2);
      // The case: its back and shelves, leaning on the wall or fallen flat.
      k.at('box', p.x, 0, p.z, tilt * 0.6, yaw, 0, 0.9, 1.1, 0.06, wood);
      for (const yy of [0.05, 0.4, 0.75]) k.at('box', p.x + f.nx * 0.12, yy * Math.cos(tilt * 0.6), p.z + f.nz * 0.12, tilt * 0.6, yaw, 0, 0.9, 0.04, 0.26, wood);
      for (let j = 0; j < 6; j++) {
        const b = onFace(f, 0.75 + rnd() * 0.35, (rnd() - 0.5) * 1.0);
        k.at('box', b.x, 0, b.z, (rnd() - 0.5) * 0.3, rnd() * TAU, (rnd() < 0.5 ? Math.PI / 2 : 0) + (rnd() - 0.5) * 0.3, 0.16, 0.05, 0.22, pick(rnd, BOOKS));
      }
    }
    for (const f of every(rm.faces, 4, rm.i + 2)) {
      const p = onFace(f, 0.3, (rnd() - 0.5) * 0.4);
      const n = 3 + Math.floor(rnd() * 6);
      for (let j = 0; j < n; j++) k.at('box', p.x, j * 0.052, p.z, 0, (rnd() - 0.5) * 0.5, 0, 0.18 + rnd() * 0.05, 0.05, 0.24, pick(rnd, BOOKS));
      candles(k, rnd, onFace(f, 0.55, 0.35), 2, '#d0d6de', '#fff4d8');
      // Red wax dripped on the floor and on the books.
      for (let j = 0; j < 3; j++) k.at('ball', p.x + (rnd() - 0.5) * 0.5, n * 0.052 * rnd(), p.z + (rnd() - 0.5) * 0.5, 0, 0, 0, 0.04, 0.012, 0.04, '#b0141c', 0.25, 0.6);
    }
    for (const f of every(rm.faces, 7, rm.i + 4)) {
      const p = onFace(f, 0.6);
      k.at('cyl8', p.x, 0, p.z, Math.PI / 2, rnd() * TAU, 0, 0.06, 0.1, 0.06, '#101014', 0, 0.8);
    }
  }
};

const forge: Dresser = (k, { rooms, rnd, emitters }, out) => {
  for (const rm of rooms) {
    // Coal heaps glowing at their hearts, chains slung from the wall tops, cold crucibles, verdigris pipes.
    for (const f of every(rm.faces, 4, rm.i)) {
      const p = onFace(f, 0.4, (rnd() - 0.5) * 0.4);
      for (let j = 0; j < 6; j++) {
        const s = 0.06 + rnd() * 0.07;
        k.at('rock', p.x + (rnd() - 0.5) * 0.45, s * 0.4, p.z + (rnd() - 0.5) * 0.45, rnd() * 3, rnd() * 3, 0, s, s * 0.7, s, j < 2 ? '#ff3a12' : '#151312', j < 2 ? 1.6 : 0);
      }
    }
    for (const f of every(rm.faces, 3, rm.i + 1)) {
      const a = onFace(f, 0.02, -0.5, WALL_TOP + 0.05);
      const b = onFace(f, 0.02, 0.5, WALL_TOP + 0.05);
      const n = 9;
      for (let j = 0; j <= n; j++) {
        const t = j / n;
        const p = a.clone().lerp(b, t);
        p.y -= Math.sin(t * Math.PI) * 0.35;
        p.addScaledVector(new THREE.Vector3(f.nx, 0, f.nz), 0.05);
        k.at('link', p.x, p.y, p.z, 0, yawOf(f) + Math.PI / 2 + (j % 2) * (Math.PI / 2), Math.PI / 2, 0.04, 0.055, 0.04, '#26262a', 0, 0.6);
      }
    }
    for (const f of every(rm.faces, 6, rm.i + 3)) {
      const a = onFace(f, 0.12, -0.6, 0.5);
      const b = onFace(f, 0.12, 0.6, 0.5);
      k.seg('cyl8', a, b, 0.06, '#3f7a68', 0, 0.4);
      k.at('cyl8', b.x, 0, b.z, 0, 0, 0, 0.06, 0.5, 0.06, '#3f7a68', 0, 0.4);
    }
  }
  // Cold anvils about the rooms (the bellows' own come with it: SimaLook draws them as they are placed).
  const anvils: [number, number][] = [];
  for (const rm of rooms) if (rm.kind === 'room' && anvils.length < 3 && rm.rim.length) anvils.push(pick(rnd, rm.rim));
  for (const [x, z] of anvils) {
    k.at('anvil', x, 0, z, 0, rnd() * TAU, 0, 0.8, 0.8, 0.8, '#2e2e33', 0, 0.75);
    k.at('box', x, 0, z, 0, rnd() * TAU, 0, 0.5, 0.06, 0.5, '#1a1010');
  }
  // Trip hammers: a frame over each hammer spot; the head is an instance that falls (SimaLook).
  for (const z of emitters) if (z.haz === 'hammer') {
    const x = z.x * K, y = z.y * K;
    const yaw = rnd() * TAU;
    const ox = Math.cos(yaw) * 0.6, oz = Math.sin(yaw) * 0.6;
    for (const sgn of [-1, 1]) k.at('box', x + ox * sgn, 0, y + oz * sgn, 0, -yaw, 0, 0.14, 1.9, 0.14, '#241d18');
    k.seg('box', new THREE.Vector3(x - ox * 1.1, 1.85, y - oz * 1.1), new THREE.Vector3(x + ox * 1.1, 1.85, y + oz * 1.1), 0.12, '#2a221c');
    k.at('cyl8', x, 0, y, 0, 0, 0, 0.42, 0.04, 0.42, '#1a1414', 0, 0.3);
    out.hammers.push(new THREE.Vector3(x, 0, y));
  }
};

const nest: Dresser = (k, { rooms, rnd }) => {
  for (const rm of rooms) {
    // Silk sheets over the walls, threads strung from wall top to wall top across the rim, cocoons hanging, eggs.
    for (const f of every(rm.faces, 2, rm.i)) {
      const p = onFace(f, 0.04, (rnd() - 0.5) * 0.3, 0.38);
      k.at('plane', p.x, p.y, p.z, -0.25, yawOf(f), (rnd() - 0.5) * 0.4, 0.9 + rnd() * 0.4, 0.8, 1, shade('#dcd6ec', rnd, 0.1), 0.12);
    }
    const fs = every(rm.faces, 3, rm.i + 1);
    for (let j = 0; j + 1 < fs.length; j++) {
      const a = onFace(fs[j] as Face, -0.1, 0, WALL_TOP + 0.05);
      const b = onFace(fs[j + 1] as Face, -0.1, 0, WALL_TOP + 0.05);
      if (a.distanceTo(b) > 3.2) continue;
      const mid = a.clone().lerp(b, 0.5);
      mid.y -= 0.15;
      k.seg('box', a, mid, 0.008, '#f0ecfa', 0.5);
      k.seg('box', mid, b, 0.008, '#f0ecfa', 0.5);
      if (rnd() < 0.7) {
        const l = 0.15 + rnd() * 0.2;
        k.seg('box', mid, mid.clone().setY(mid.y - l), 0.006, '#f0ecfa', 0.4);
        k.at('egg', mid.x, mid.y - l - 0.22, mid.z, (rnd() - 0.5) * 0.2, rnd() * TAU, 0, 0.11, 0.12, 0.11, shade('#e6e0f2', rnd, 0.12), 0.08);
      }
    }
    for (const f of every(rm.faces, 4, rm.i + 2)) {
      const p = onFace(f, 0.4, (rnd() - 0.5) * 0.4);
      for (let j = 0; j < 6; j++) k.at('ball', p.x + (rnd() - 0.5) * 0.3, 0.04, p.z + (rnd() - 0.5) * 0.3, 0, 0, 0, 0.05, 0.045, 0.05, '#ece6f6', 0.12);
      k.at('egg', p.x + 0.2, 0.16, p.z, 0.3, rnd() * TAU, Math.PI / 2 - 0.3, 0.12, 0.13, 0.12, '#d8d0ea', 0.06);
    }
  }
};

/** A rib: a curved bone rising out of the wall top and bending in over the room's rim. */
const rib = (k: Kit, f: Face, s: number, h: number, reach: number, rnd: () => number): void => {
  const n = 7;
  let prev = onFace(f, -0.25, s, 0);
  const c = shade('#9a9ea6', rnd, 0.12);
  for (let j = 1; j <= n; j++) {
    const t = j / n;
    const p = onFace(f, -0.25 + Math.sin(t * Math.PI * 0.5) ** 1.6 * reach, s, Math.sin(t * Math.PI * 0.62) * h);
    k.seg('cyl6', prev, p, 0.19 * (1 - t * 0.5), c);
    prev = p;
  }
  k.at('ball', prev.x, prev.y, prev.z, 0, 0, 0, 0.1, 0.1, 0.1, c);
};

const ribs: Dresser = (k, { rooms, rnd, emitters }) => {
  for (const rm of rooms) {
    // The titan's ribs: great grey arches out of the walls, bending in over the rim, pairs facing each other.
    for (const f of every(rm.faces, 3, rm.i)) rib(k, f, 0, rm.kind === 'boss' ? 2.9 : 2.3, rm.kind === 'boss' ? 1.9 : 1.5, rnd);
    // Knuckles of vertebra and lumps of flesh along the walls.
    for (const f of every(rm.faces, 3, rm.i + 1)) {
      const p = onFace(f, 0.3, (rnd() - 0.5) * 0.5);
      k.at('cyl6', p.x, 0, p.z, Math.PI / 2, yawOf(f), 0, 0.14, 0.2, 0.14, BONE_D);
      k.at('rock', p.x + f.nz * 0.3, 0.05, p.z - f.nx * 0.3, 0, rnd() * 3, 0, 0.18, 0.1, 0.16, '#4a1218', 0, 0.6);
    }
  }
  // The rockfall spots: the floor cracked and a heap of fallen stone and bone.
  for (const z of emitters) if (z.haz === 'rockfall') {
    const p = new THREE.Vector3(z.x * K, 0, z.y * K);
    rubble(k, rnd, p.clone().add(new THREE.Vector3(0.5, 0, 0.3)), 4, '#5a4a4c', 0.4);
  }
};

const DRESS: Record<StratumId, Dresser> = { ossuary, marrow, glass, cisterns, buried, grotto, frost, geode, archive, forge, nest, ribs };

/** Builds a floor's props for its stratum: one geometry (one draw), the trip hammers' spots and the marks that glow. */
export const buildProps = (stratum: StratumId, ctx: PropCtx): Built => {
  const k = new Kit();
  const out: Built = { geometry: new THREE.BufferGeometry(), hammers: [], marks: [] };
  DRESS[stratum](k, ctx, out);
  // Never empty: one speck far under the floor keeps the mesh drawable (and compiled).
  if (k.empty) k.at('box', 0, -50, 0, 0, 0, 0, 0.001, 0.001, 0.001, '#000000');
  out.geometry = k.geometry();
  return out;
};
